import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Profile from './pages/Profile';
import QuizzesOverview from './pages/QuizzesOverview';
import FAQ from './pages/FAQ';
import Videos from './pages/Videos';
import AdminPage from './pages/AdminPage';
import AdminUpdates from './pages/AdminUpdates';
import AdminRolesPage from './pages/AdminRolesPage';
import AdminThemePage from './pages/AdminThemePage';
import QuizEditorPage from './pages/QuizEditorPage';
import Start from './pages/Start';
import ModulePage from './pages/ModulePage';
import ModuleUnit from './pages/ModuleUnit';
import UnitLoader from './pages/UnitLoader';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AppShell from './components/AppShell';

const ProtectedRoute: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { token } = useAuth();
  return token
    ? (children as React.ReactElement)
    : React.createElement(Navigate, { to: '/login', replace: true });
};

const PermissionRoute: React.FC<React.PropsWithChildren<{ anyOf: string[] }>> = ({ children, anyOf }) => {
  const { token, hasPermission } = useAuth();
  if (!token) {
    return React.createElement(Navigate, { to: '/login', replace: true });
  }
  const allowed = anyOf.some((perm) => hasPermission(perm));
  if (!allowed) {
    return React.createElement(Navigate, { to: '/', replace: true });
  }
  return (children as React.ReactElement) ?? React.createElement(Navigate, { to: '/', replace: true });
};

type ProtectedOptions = {
  fullBleed?: boolean;
};

const renderProtected = (
  component: React.ReactElement,
  options?: ProtectedOptions
) =>
  React.createElement(
    ProtectedRoute,
    null,
    React.createElement(
      AppShell,
      options?.fullBleed ? { fullBleed: true } : null,
      component
    )
  );

const renderPermissionProtected = (component: React.ReactElement, anyOf: string[]) =>
  React.createElement(
    PermissionRoute,
    { anyOf },
    React.createElement(AppShell, null, component)
  );

const AppClean: React.FC = () =>
  React.createElement(
    ThemeProvider,
    null,
    React.createElement(
      AuthProvider,
      null,
      React.createElement(
        Router,
        null,
        React.createElement(
          Routes,
          null,
          React.createElement(Route, {
          path: '/login',
          element: React.createElement(Login, null)
        }),
          React.createElement(Route, {
            path: '/login-error',
            element: React.createElement(Login, null)
          }),
        React.createElement(Route, {
          path: '/',
          element: renderProtected(React.createElement(Start, null), {
            fullBleed: true
          })
        }),
        React.createElement(Route, {
          path: '/profile',
          element: renderProtected(React.createElement(Profile, null))
        }),
        React.createElement(Route, {
          path: '/quizzes',
          element: renderProtected(React.createElement(QuizzesOverview, null))
        }),
        React.createElement(Route, {
          path: '/modules/:modId',
          element: renderProtected(React.createElement(ModulePage, null))
        }),
        React.createElement(Route, {
          path: '/modules/:modId/:unitId',
          element: renderProtected(React.createElement(ModuleUnit, null))
        }),
        React.createElement(Route, {
          path: '/faq',
          element: renderProtected(React.createElement(FAQ, null))
        }),
        React.createElement(Route, {
          path: '/videos',
          element: renderProtected(React.createElement(Videos, null))
        }),
        React.createElement(Route, {
          path: '/admin',
          element: renderPermissionProtected(React.createElement(AdminPage, null), [
            'access_admin'
          ])
        }),
        React.createElement(Route, {
          path: '/admin/roles',
          element: renderPermissionProtected(React.createElement(AdminRolesPage, null), [
            'manage_roles_legend',
            'view_roles_legend'
          ])
        }),
        React.createElement(Route, {
          path: '/admin/updates',
          element: renderPermissionProtected(React.createElement(AdminUpdates, null), [
            'manage_updates'
          ])
        }),
        React.createElement(Route, {
          path: '/admin/theme',
          element: renderPermissionProtected(React.createElement(AdminThemePage, null), [
            'manage_theme_settings'
          ])
        }),
        React.createElement(Route, {
          path: '/admin/quiz-editor',
          element: renderPermissionProtected(React.createElement(QuizEditorPage, null), [
            'manage_quiz_questions',
            'manage_quiz'
          ])
        }),
        React.createElement(Route, {
          path: '/units/:page',
          element: renderProtected(React.createElement(UnitLoader, null), {
            fullBleed: true
          })
        })
      )
    )
  )
);

export default AppClean;
